import {
    ArrowLeft,
    Building,
    Calendar,
    DollarSign,
    FileText,
    Save,
    User,
} from "lucide-react";
import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";

const RMCContractAdd = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    unit_id: "",
    tenant_id: "",
    contract_number: "",
    contract_type: "monthly",
    contract_category: "",
    start_date: "",
    end_date: "",
    move_in_date: "",
    move_out_date: "",
    renewal_date: "",
    notice_period: 30,
    monthly_rent: "",
    deposit: "",
    maintenance_fee: "",
    parking_fee: "",
    due_date: 1,
    late_fee_rate: 0.1,
    grace_period: 3,
    max_occupants: "",
    pet_allowed: false,
    smoking_allowed: false,
    parking_spaces: "",
    utility_included: "",
    status: "active",
    contract_file_path: "",
    contract_signed_date: "",
    contract_verified: false,
    notes: "",
  });

  const [errors, setErrors] = useState({});

  // 임대사업자 목록 조회
  const { data: propertyOwners } = useQuery(
    "rmc-property-owners",
    async () => {
      const response = await fetch("/api/rmc/property-owners", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });

      if (!response.ok) {
        throw new Error("임대사업자 목록을 불러올 수 없습니다.");
      }

      return response.json();
    }
  );

  // 건물 목록 조회 (선택된 임대사업자에 따라)
  const { data: buildings } = useQuery(
    `rmc-buildings-${formData.property_owner_id}`,
    async () => {
      if (!formData.property_owner_id) return { buildings: [] };

      const response = await fetch(
        `/api/rmc/buildings?property_owner_id=${formData.property_owner_id}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("건물 목록을 불러올 수 없습니다.");
      }

      return response.json();
    },
    {
      enabled: !!formData.property_owner_id,
    }
  );

  // 세대 목록 조회 (선택된 건물에 따라)
  const { data: units } = useQuery(
    `rmc-building-units-${formData.building_id}`,
    async () => {
      if (!formData.building_id) return { units: [] };

      const response = await fetch(
        `/api/rmc/buildings/${formData.building_id}/units`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("세대 목록을 불러올 수 없습니다.");
      }

      return response.json();
    },
    {
      enabled: !!formData.building_id,
    }
  );

  // 계약 등록 뮤테이션
  const addContractMutation = useMutation(
    async (contractData) => {
      const response = await fetch("/api/rmc/contracts/", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(contractData),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "계약 등록에 실패했습니다.");
      }

      return response.json();
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries("rmc-contracts");
        queryClient.invalidateQueries("rmc-contracts-statistics");
        alert("계약이 성공적으로 등록되었습니다.");
        navigate("/rmc/contracts");
      },
      onError: (error) => {
        alert(`계약 등록 실패: ${error.message}`);
      },
    }
  );

  // 폼 데이터 변경 핸들러
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // 에러 초기화
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = (e) => {
    e.preventDefault();

    // 유효성 검사
    const newErrors = {};
    if (!formData.unit_id) newErrors.unit_id = "세대를 선택해주세요.";
    if (!formData.contract_number) newErrors.contract_number = "계약번호를 입력해주세요.";
    if (!formData.start_date) newErrors.start_date = "계약 시작일을 입력해주세요.";
    if (!formData.end_date) newErrors.end_date = "계약 종료일을 입력해주세요.";
    if (!formData.monthly_rent) newErrors.monthly_rent = "월 임대료를 입력해주세요.";

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // 숫자 필드 변환
    const submitData = {
      ...formData,
      unit_id: parseInt(formData.unit_id),
      tenant_id: formData.tenant_id ? parseInt(formData.tenant_id) : null,
      notice_period: parseInt(formData.notice_period),
      monthly_rent: parseInt(formData.monthly_rent),
      deposit: formData.deposit ? parseInt(formData.deposit) : null,
      maintenance_fee: formData.maintenance_fee ? parseInt(formData.maintenance_fee) : null,
      parking_fee: formData.parking_fee ? parseInt(formData.parking_fee) : null,
      due_date: parseInt(formData.due_date),
      late_fee_rate: parseFloat(formData.late_fee_rate),
      grace_period: parseInt(formData.grace_period),
      max_occupants: formData.max_occupants ? parseInt(formData.max_occupants) : null,
      parking_spaces: formData.parking_spaces ? parseInt(formData.parking_spaces) : null,
    };

    addContractMutation.mutate(submitData);
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* 헤더 */}
      <div className="flex items-center space-x-4">
        <button
          onClick={() => navigate("/rmc/contracts")}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">계약 등록</h1>
          <p className="text-gray-600">
            새로운 임대 계약을 등록합니다.
          </p>
        </div>
      </div>

      {/* 계약 등록 폼 */}
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* 기본 정보 */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center space-x-2 mb-6">
            <FileText className="w-5 h-5 text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">기본 정보</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* 계약번호는 자동생성되므로 제거 */}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                계약 유형
              </label>
              <select
                name="contract_type"
                value={formData.contract_type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
            <option value="monthly">월세</option>
            <option value="yearly">연세</option>
            <option value="jeonse">전세제</option>
            <option value="deposit">삭월세</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                계약 분류
              </label>
              <input
                type="text"
                name="contract_category"
                value={formData.contract_category}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: 일반임대, 단기임대"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                계약 상태
              </label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="active">활성</option>
                <option value="expired">만료</option>
                <option value="terminated">종료</option>
              </select>
            </div>
          </div>
        </div>

        {/* 건물 및 세대 정보 */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center space-x-2 mb-6">
            <Building className="w-5 h-5 text-green-600" />
            <h2 className="text-lg font-semibold text-gray-900">건물 및 세대 정보</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                임대사업자
              </label>
              <select
                name="property_owner_id"
                value={formData.property_owner_id || ""}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">임대사업자를 선택하세요</option>
                {propertyOwners?.property_owners?.map((owner) => (
                  <option key={owner.id} value={owner.id}>
                    {owner.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                건물
              </label>
              <select
                name="building_id"
                value={formData.building_id || ""}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                disabled={!formData.property_owner_id}
              >
                <option value="">건물을 선택하세요</option>
                {buildings?.buildings?.map((building) => (
                  <option key={building.id} value={building.id}>
                    {building.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                세대 <span className="text-red-500">*</span>
              </label>
              <select
                name="unit_id"
                value={formData.unit_id}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.unit_id ? "border-red-500" : "border-gray-300"
                }`}
                disabled={!formData.building_id}
              >
                <option value="">세대를 선택하세요</option>
                {units?.units?.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unit_number}호 ({unit.area}㎡, {unit.unit_type})
                  </option>
                ))}
              </select>
              {errors.unit_id && (
                <p className="mt-1 text-sm text-red-600">{errors.unit_id}</p>
              )}
            </div>
          </div>
        </div>

        {/* 계약 기간 */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center space-x-2 mb-6">
            <Calendar className="w-5 h-5 text-purple-600" />
            <h2 className="text-lg font-semibold text-gray-900">계약 기간</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                계약 시작일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="start_date"
                value={formData.start_date}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.start_date ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.start_date && (
                <p className="mt-1 text-sm text-red-600">{errors.start_date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                계약 종료일 <span className="text-red-500">*</span>
              </label>
              <input
                type="date"
                name="end_date"
                value={formData.end_date}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.end_date ? "border-red-500" : "border-gray-300"
                }`}
              />
              {errors.end_date && (
                <p className="mt-1 text-sm text-red-600">{errors.end_date}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                입주일
              </label>
              <input
                type="date"
                name="move_in_date"
                value={formData.move_in_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                퇴거일
              </label>
              <input
                type="date"
                name="move_out_date"
                value={formData.move_out_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                갱신일
              </label>
              <input
                type="date"
                name="renewal_date"
                value={formData.renewal_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                해지 통보 기간 (일)
              </label>
              <input
                type="number"
                name="notice_period"
                value={formData.notice_period}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
              />
            </div>
          </div>
        </div>

        {/* 금액 정보 */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center space-x-2 mb-6">
            <DollarSign className="w-5 h-5 text-yellow-600" />
            <h2 className="text-lg font-semibold text-gray-900">금액 정보</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                월 임대료 <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="monthly_rent"
                value={formData.monthly_rent}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                  errors.monthly_rent ? "border-red-500" : "border-gray-300"
                }`}
                placeholder="예: 500000"
              />
              {errors.monthly_rent && (
                <p className="mt-1 text-sm text-red-600">{errors.monthly_rent}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                보증금
              </label>
              <input
                type="number"
                name="deposit"
                value={formData.deposit}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: 10000000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                관리비
              </label>
              <input
                type="number"
                name="maintenance_fee"
                value={formData.maintenance_fee}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: 50000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                주차비
              </label>
              <input
                type="number"
                name="parking_fee"
                value={formData.parking_fee}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: 100000"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                납부일
              </label>
              <input
                type="number"
                name="due_date"
                value={formData.due_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
                max="31"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                연체료율 (%)
              </label>
              <input
                type="number"
                name="late_fee_rate"
                value={formData.late_fee_rate}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                step="0.01"
                min="0"
                max="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                연체 유예 기간 (일)
              </label>
              <input
                type="number"
                name="grace_period"
                value={formData.grace_period}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
              />
            </div>
          </div>
        </div>

        {/* 계약 조건 */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <div className="flex items-center space-x-2 mb-6">
            <User className="w-5 h-5 text-indigo-600" />
            <h2 className="text-lg font-semibold text-gray-900">계약 조건</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                최대 거주 인원
              </label>
              <input
                type="number"
                name="max_occupants"
                value={formData.max_occupants}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="1"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                주차 공간 수
              </label>
              <input
                type="number"
                name="parking_spaces"
                value={formData.parking_spaces}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                min="0"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                포함 공과금
              </label>
              <input
                type="text"
                name="utility_included"
                value={formData.utility_included}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: 수도, 전기, 가스"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="pet_allowed"
                  checked={formData.pet_allowed}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  반려동물 허용
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="smoking_allowed"
                  checked={formData.smoking_allowed}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  흡연 허용
                </label>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="contract_verified"
                  checked={formData.contract_verified}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-900">
                  계약서 검증 완료
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* 기타 정보 */}
        <div className="bg-white p-6 rounded-lg shadow-sm border">
          <h2 className="text-lg font-semibold text-gray-900 mb-6">기타 정보</h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                계약서 파일 경로
              </label>
              <input
                type="text"
                name="contract_file_path"
                value={formData.contract_file_path}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="예: /contracts/2024/C2024001.pdf"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                계약서 서명일
              </label>
              <input
                type="date"
                name="contract_signed_date"
                value={formData.contract_signed_date}
                onChange={handleInputChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                비고
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="계약 관련 특이사항이나 추가 정보를 입력하세요."
              />
            </div>
          </div>
        </div>

        {/* 제출 버튼 */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate("/rmc/contracts")}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
          >
            취소
          </button>
          <button
            type="submit"
            disabled={addContractMutation.isPending}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="w-4 h-4" />
            <span>{addContractMutation.isPending ? "등록 중..." : "계약 등록"}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default RMCContractAdd;
